import { t } from "components/CentralDataMangement/translation/Translation";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import React, { FormEvent, useEffect, useState } from "react";
import { applicationOptions } from "./view-scrap-interface";
import Button from "components/common/atoms/Button";
import LabelField from "components/common/atoms/LabelField";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiCall } from "components/common/services/ApiServices";
import {
  CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
  COMPANY_MICROSERVICE,
} from "Constants";
import {
  PHONE_CALL_URL,
  STORE_SCRAPING_COM_CONTACTS,
  STORE_VACANCY_SCRAP,
} from "../../../routes/ApiEndpoints";
import { formatPhoneFor3CX } from "components/common/utlis/HelperFunctions";
import Title from "components/common/atoms/Title";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import CustomNotify from "components/common/atoms/CustomNotify";

const GET_COMPANY_DETAILS_PATH = "get/company/details";

/**
 * Contact section component that displays contact information
 * @param contact - Contact object containing contact details
 * @param index - Index of the contact (0 for primary, 1+ for secondary)
 */
const ContactSection = ({
  contact,
  index,
}: {
  contact: any;
  index: number;
}) => {
  const title =
    index === 0 ? t("Primary Contact") : `${t("Secondary Contact")} ${index}`;

  return (
    <div className="row mt-3">
      <div className="col-12">
        <LabelField title={title} className="tab-subtitle secondary-title" />
      </div>
      <div
        className="col-sm-12 col-md-3"
        onClick={() => {
          const phone = String(contact?.telephone || "").trim();
          if (phone) {
            window.open(
              `${PHONE_CALL_URL}${formatPhoneFor3CX(phone).replace(
                "+",
                "%2B"
              )}`,
              "_blank"
            );
          }
        }}
        style={{ cursor: contact?.telephone ? "pointer" : "default" }}
      >
        <LabelWithInputField
          isMandatory={false}
          name={`contact-${index}-telephone`}
          handleChange={() => {}}
          value={contact?.telephone || ""}
          isDisabled={false}
          readOnly={true}
          id={`contact-${index}-telephone`}
          label={t("Telephone")}
          type="text"
          placeholder={t("Telephone")}
          customStyle={{ cursor: "pointer" }}
        />
      </div>
      <div
        className="col-sm-12 col-xxl-3 col-md-3"
        onClick={() => {
          const email = String(contact?.email || "").trim();
          if (email) {
            window.location.href = `mailto:${email}`;
          }
        }}
        style={{ cursor: contact?.email ? "pointer" : "default" }}
      >
        <LabelWithInputField
          isMandatory={false}
          name={`contact-${index}-email`}
          handleChange={() => {}}
          value={contact?.email || ""}
          isDisabled={false}
          readOnly={true}
          id={`contact-${index}-email`}
          label={t("Email")}
          type="text"
          placeholder={t("Email")}
          customStyle={{ cursor: "pointer" }}
        />
      </div>
      <div className="col-sm-12 col-xxl-3 col-md-3">
        <LabelWithInputField
          isMandatory={false}
          name={`contact-${index}-role`}
          handleChange={() => {}}
          value={contact?.role || ""}
          isDisabled={true}
          id={`contact-${index}-role`}
          label={t("Title - position")}
          type="text"
          placeholder={t("Title - position")}
        />
      </div>
      <div className="col-sm-12 col-xxl-3 col-md-3">
        <LabelWithInputField
          isMandatory={false}
          name={`contact-${index}-name`}
          handleChange={() => {}}
          value={`${contact?.fName || ""} ${contact?.lName || ""}`.trim()}
          isDisabled={true}
          id={`contact-${index}-name`}
          label={t("Contact person")}
          type="text"
          placeholder={t("Contact person")}
        />
      </div>
    </div>
  );
};

/**
 * Main component for viewing and managing scraped company data
 * Allows users to create companies, vacancies, and save contacts
 */
const ViewScrapping: React.FC = () => {
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [isSavingContacts, setIsSavingContacts] = useState<boolean>(false);
  const [isCreatingVacancy, setIsCreatingVacancy] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /**
   * Fetches scrapping data and associated company details from API
   */
  const fetchScrappingData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      // Fetch main scrapping data
      const scrappingResponse = await ApiCall.getService(
        `fetch-scrapping-data/${id}`,
        "GET",
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
        false
      );

      if (scrappingResponse?.status === 200) {
        const scrappingData = scrappingResponse?.data;

        // Transform status value for display
        const statusVal = scrappingData?.basic?.status;
        const statusOption = applicationOptions.find(
          // BG status 1 (Completed) maps to frontend status value 2
          // BG status 0 (New) maps to frontend status value 1
          (opt) => opt.value === (statusVal === 1 ? 2 : 1)
        );

        let completeFormData = {
          ...scrappingData,
          status: statusOption ?? null,
          existingCompanyDetails: null,
        };

        // Fetch existing company details if available
        const existingCompanyId = scrappingData?.main?.existing_company_id;
        if (existingCompanyId) {
          try {
            const companyDetailsResponse = await ApiCall.getService(
              `${GET_COMPANY_DETAILS_PATH}/${existingCompanyId}`,
              "GET",
              COMPANY_MICROSERVICE,
              false
            );
            if (companyDetailsResponse?.status === 200) {
              completeFormData.existingCompanyDetails =
                companyDetailsResponse.data;
            } else {
              CustomNotify({
                type: "error",
                message: t("Failed to fetch company details"),
              });
            }
          } catch (companyError) {
            console.error(
              `Error fetching company details for ID ${existingCompanyId}:`,
              companyError
            );
          }
        }
        setFormData(completeFormData);
      } else {
        CustomNotify({
          type: "error",
          message: t("Failed to load scrapping data"),
        });
      }
    } catch (error) {
      console.error("Error fetching scrapping data:", error);
      CustomNotify({
        type: "error",
        message: t("Error loading scrapping data"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScrappingData();
  }, [id]);

  const initialErrors: { [key: string]: string } = {};
  const [_errors, _setErrors] = useState<{ [key: string]: string }>(
    initialErrors
  );

  /**
   * Handles dropdown selection changes
   */
  const handleSelectChange = (selectedOption: any, name: string) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [name]: selectedOption,
    }));
  };

  /**
   * Handles input field changes
   */
  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  /**
   * Navigates to company matching/creation page
   * @param scrapingIdForUrl - ID of the scraping record
   * @param companyIdForUrl - Optional existing company ID
   */
  const navigateToMatching = (
    scrapingIdForUrl: string | number | null,
    companyIdForUrl?: string | number | null
  ) => {
    if (!scrapingIdForUrl) {
      CustomNotify({
        type: "error",
        message: t("Navigation error: Missing required ID"),
      });
      return;
    }

    let finalCompanyId = companyIdForUrl;

    // Use existing company ID from form data if not provided
    if (!finalCompanyId && formData?.main?.existing_company_id) {
      finalCompanyId = formData.main.existing_company_id;
    }

    // Navigate to appropriate page based on company existence
    let url;
    if (finalCompanyId) {
      url = `/company/${finalCompanyId}?tabId=contacts`;
    } else {
      url = `/company?tabId=basic&scraping_id=${scrapingIdForUrl}`;
    }

    navigate(url);
  };

  // Status and vacancy logic for button display
  const shouldShowCreateCompany =
    formData?.basic?.company_status ||
    formData?.main?.extracted_company_id !== null;
  // CR-581 (Req 4): prefer the authoritative backend "has_vacancy" flag so the converted/
  // read-only state survives a page refresh. Falls back to the persisted status flag.
  // (When truthy => already converted, so the "Create Vacancy" button is hidden below.)
  const shouldShowCreateVacancy =
    formData?.main?.has_vacancy ?? formData.basic?.status;

  // Contact management logic
  const companyStatusIsCompleted = formData.status?.value === 2;
  const totalScrapedContacts =
    (formData.primaryContact ? 1 : 0) +
    (formData.secondaryContacts?.length || 0);
  const hasContactsToSave = totalScrapedContacts > 0;
  const shouldShowAddContactButton =
    companyStatusIsCompleted && hasContactsToSave;

  /**
   * Creates a new vacancy from scraped data
   */
  const handleCreateVacancy = async () => {
    if (!id || !formData) {
      CustomNotify({
        type: "error",
        message: t("Missing required data. Cannot create vacancy."),
      });
      return;
    }

    // Determine company and location IDs
    let companyId = null;
    let locationId = null;

    if (formData?.main?.extracted_company_id) {
      companyId = formData.main.extracted_company_id;
    } else if (formData?.main?.existing_company_id) {
      companyId = formData.main.existing_company_id;
    } else if (formData?.existingCompanyDetails?.id) {
      companyId = formData.existingCompanyDetails.id;
    }

    if (formData?.main?.extracted_location_id) {
      locationId = formData.main.extracted_location_id;
    } else if (formData?.existingCompanyDetails?.location_id) {
      locationId = formData.existingCompanyDetails.location_id;
    }

    if (!companyId) {
      CustomNotify({
        type: "error",
        message: t("Company ID is required. Cannot create vacancy."),
      });
      return;
    }

    setIsCreatingVacancy(true);

    try {
      const scrapingPayload = {
        scrapingId: id,
        companyId: companyId,
        company: formData?.basic?.company || "",
        position: formData?.basic?.position || "",
        advertisementLink: formData?.basic?.advertisement_link || "",
        locationId: locationId,
      };

      const scrapingResponse = await ApiCall.service(
        STORE_VACANCY_SCRAP,
        "POST",
        scrapingPayload,
        false,
        "central-data-management"
      );

      if (
        scrapingResponse?.status === 200 ||
        scrapingResponse?.status === 201
      ) {
        CustomNotify({
          type: "success",
          message: t("Vacancy created successfully!"),
        });

        // Update form data with new vacancy ID if available
        if (scrapingResponse?.data?.vacancyId || scrapingResponse?.data?.id) {
          const newVacancyId =
            scrapingResponse?.data?.vacancyId || scrapingResponse?.data?.id;
          setFormData((prevData: any) => ({
            ...prevData,
            main: {
              ...prevData.main,
              vacancy_id: newVacancyId,
              has_vacancy: true,
            },
          }));
        } else {
          // Refresh data if no ID returned
          fetchScrappingData();
        }
      } else {
        const errorMessage =
          scrapingResponse?.data?.msg ||
          scrapingResponse?.data?.message ||
          t("Failed to create vacancy");
        CustomNotify({
          type: "error",
          message: errorMessage,
        });
      }
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.msg ||
        (error as any)?.message ||
        t("An unexpected error occurred while creating vacancy.");
      CustomNotify({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsCreatingVacancy(false);
    }
  };

  /**
   * Saves scraped contacts to the system
   */
  const handleSaveContacts = async () => {
    if (!id) {
      CustomNotify({
        type: "error",
        message: t("Missing scraping ID. Cannot save contacts."),
      });
      return;
    }

    // Collect all contacts from form data
    const contactsFromFormData: any[] = [];
    if (formData.primaryContact) {
      contactsFromFormData.push(formData.primaryContact);
    }
    if (formData.secondaryContacts && formData.secondaryContacts.length > 0) {
      contactsFromFormData.push(...formData.secondaryContacts);
    }

    if (contactsFromFormData.length === 0) {
      CustomNotify({
        type: "warning",
        message: t("No contacts available to save"),
      });
      return;
    }

    // Transform contacts for API
    const contactsToStore = contactsFromFormData.map((contact: any) => ({
      id: contact.id || null,
      fName: contact.fName || "",
      lName: contact.lName || "",
      email: contact.email || "",
      phNumber: contact.telephone || null,
      functionTitle: contact.role || null,
    }));

    setIsSavingContacts(true);

    try {
      // Clean VAT number for storage
      let vatNumber = "";
      if (formData?.basic?.vat1) {
        vatNumber = String(formData.basic.vat1)
          .replace(/^FR/i, "")
          .replace(/^BE/i, "")
          .replace(/\./g, "")
          .replace(/-/g, "");
      }

      const payload = {
        scrapingId: id,
        contacts: contactsToStore,
        vatNumber: vatNumber,
      };

      const response = await ApiCall.service(
        STORE_SCRAPING_COM_CONTACTS,
        "POST",
        payload,
        true,
        COMPANY_MICROSERVICE
      );

      if (response?.status === 200 || response?.status === 201) {
        CustomNotify({
          type: "success",
          message: t("Contacts saved successfully!"),
        });

        // Determine company ID for navigation
        let finalCompanyIdForNav: string | number | null = null;
        if (formData?.main?.existing_company_id) {
          finalCompanyIdForNav = formData.main.existing_company_id;
        } else if (response?.data?.companyId) {
          finalCompanyIdForNav = response?.data?.companyId;
        }

        navigateToMatching(id, finalCompanyIdForNav);
      } else {
        const errorMessage =
          response.data?.msg ||
          response.data?.message ||
          t("Unknown error occurred");
        CustomNotify({
          type: "error",
          message: errorMessage,
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.msg ||
        error.message ||
        t("An unexpected error occurred.");
      CustomNotify({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSavingContacts(false);
    }
  };

  /**
   * Handles form submission (currently prevents default behavior)
   */
  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  if (loading) {
    return <LoadingIcon />;
  }

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Scraping",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Scraping")} />
      <form onSubmit={handleSubmit}>
        <div className="form-border">
          {/* Status and Action Buttons Row */}
          <div className="row">
            <div className="col-sm-12 col-xxl-4 col-md-4">
              <SelectWithSearch
                title={t("Status")}
                search={true}
                options={applicationOptions}
                onChange={(e) => handleSelectChange(e, "status")}
                isMulti={false}
                name="status"
                value={formData?.status}
                isDisabled={true}
                error={_errors.status}
                isTranslate={true}
              />
            </div>
            <div className="col-md-8 mt-35">
              {formData?.main?.id !== null && (
                <>
                  {/* Create Vacancy Button - Priority when mainStatus is 1 and no completed vacancies */}
                  {!shouldShowCreateVacancy && shouldShowCreateCompany && (
                    <Button
                      title={
                        isCreatingVacancy
                          ? t("Creating...")
                          : t("Create Vacancy")
                      }
                      handleClick={handleCreateVacancy}
                      className="form-button marginRightPoint5 shadow-none float-end"
                      disabled={isCreatingVacancy}
                    />
                  )}

                  {/* Create Company Button - Show for new items when Create Vacancy is not shown */}
                  {!shouldShowCreateCompany && (
                    <Button
                      title={t("Create Company")}
                      handleClick={() => {
                        navigateToMatching(
                          formData?.main.id,
                          formData?.main.existing_company_id
                        );
                      }}
                      className="form-button marginRightPoint5 shadow-none float-end"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Company Information Row */}
          <div className="row">
            <div className="col-sm-12 col-lg-4 col-md-3">
              <LabelWithInputField
                isMandatory={false}
                name="Company"
                handleChange={() => {}}
                value={formData?.basic?.company || ""}
                isDisabled={true}
                id="company"
                label={t("Company")}
                type="text"
                placeholder={t("Company")}
              />
            </div>
            <div className="col-sm-12 col-lg-4 col-md-3">
              <LabelWithTextAreaField
                name="function"
                handleChange={() => {}}
                label={t("Function")}
                value={formData?.basic?.position || ""}
                isDisabled={true}
                isMandatory={false}
                placeholder={t("Function")}
              />
            </div>
            <div
              className="col-sm-12 col-lg-4 col-md-3"
              onClick={() => {
                const link = String(
                  formData?.basic?.advertisement_link || ""
                ).trim();
                if (link) {
                  window.open(
                    /^https?:\/\//i.test(link) ? link : `https://${link}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
              style={{
                cursor: formData?.basic?.advertisement_link
                  ? "pointer"
                  : "default",
              }}
            >
              <LabelWithInputField
                isMandatory={false}
                name="Link to advertisement"
                handleChange={() => {}}
                value={formData?.basic?.advertisement_link || ""}
                isDisabled={false}
                readOnly={true}
                id="advertisement_link"
                label={t("Link to advertisement")}
                type="text"
                placeholder={t("Link to advertisement")}
                customStyle={{ cursor: "pointer" }}
              />
            </div>
            <div
              className="col-sm-12 col-lg-4 col-md-3"
              onClick={() => {
                const link = String(formData?.basic?.website || "").trim();
                if (link) {
                  window.open(
                    /^https?:\/\//i.test(link) ? link : `https://${link}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
              style={{
                cursor: formData?.basic?.website ? "pointer" : "default",
              }}
            >
              <LabelWithInputField
                isMandatory={false}
                name="Internet"
                handleChange={() => {}}
                value={formData?.basic?.website || ""}
                isDisabled={false}
                id="website"
                label={t("Website")}
                type="text"
                placeholder={t("Website")}
              />
            </div>
            <div className="col-sm-12 col-lg-4 col-md-3 generalTabVAT">
              <div className="input-group position-relative">
                <div className="position-absolute fieldPrefix">
                  {String(formData?.basic?.vat1 || "").startsWith("FR")
                    ? "FR"
                    : "BE"}
                </div>
                <LabelWithInputField
                  isMandatory={false}
                  name="VAT ID"
                  handleChange={() => {}}
                  value={String(formData?.basic?.vat1 || "")
                    .replace(/^FR/i, "")
                    .replace(/^BE/i, "")
                    .replace(/\./g, "")
                    .replace(/-/g, "")}
                  isDisabled={true}
                  id="vat"
                  label={t("VAT ID")}
                  type="text"
                  placeholder={t("VAT ID")}
                />
              </div>
            </div>
            <div className="col-sm-12 col-lg-4 col-md-3">
              <LabelWithInputField
                isMandatory={false}
                name="Source"
                handleChange={() => {}}
                value={formData?.main?.source || ""}
                isDisabled={true}
                id="source"
                label={t("Source")}
                type="text"
                placeholder={t("Source")}
              />
            </div>
          </div>

          {/* Contact Details Section */}
          <div className="row mt-3">
            <div className="col-md-6 col-sm-12 align-self-center">
              <LabelField
                title={t("Contact details")}
                className="tab-subtitle"
              />
            </div>
            <div className="col-md-6 col-sm-12 text-md-end align-self-center">
              {shouldShowAddContactButton && (
                <Button
                  title={isSavingContacts ? t("Saving...") : t("Add Contact")}
                  handleClick={handleSaveContacts}
                  className="form-button shadow-none"
                  disabled={isSavingContacts}
                />
              )}
            </div>
          </div>

          {/* Primary Contact Display */}
          {formData?.primaryContact && (
            <ContactSection contact={formData.primaryContact} index={0} />
          )}

          {/* Secondary Contacts Display */}
          {formData?.secondaryContacts?.length > 0 &&
            formData.secondaryContacts.map(
              (contact: any, loopIndex: number) => (
                <ContactSection
                  key={`secondary-contact-${loopIndex}`}
                  contact={contact}
                  index={loopIndex + 1}
                />
              )
            )}

          {/* No Contacts Message */}
          {!formData?.primaryContact &&
            (!formData?.secondaryContacts ||
              formData?.secondaryContacts.length === 0) && (
              <div className="row mt-2">
                <div className="col-12 mb-3">
                  <p className="text-muted">
                    {t("No contact information available")}
                  </p>
                </div>
              </div>
            )}

          {/* Address Section */}
          <div className="row mt-3">
            <div className="col-12">
              <LabelField title={t("Address")} className="tab-subtitle" />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="street"
                handleChange={() => {}}
                value={formData?.address?.street || ""}
                id="street"
                label={t("Street")}
                type="text"
                isDisabled={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="number"
                handleChange={() => {}}
                value={formData?.address?.number || ""}
                id="number"
                label={t("Number")}
                type="text"
                isDisabled={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="box"
                handleChange={() => {}}
                value={formData?.address?.box || formData?.box || ""}
                id="box"
                label={t("Box")}
                type="text"
                isDisabled={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="zipCode"
                handleChange={() => {}}
                value={formData?.address?.post_code || ""}
                id="post_code"
                label={t("Zip code")}
                type="text"
                isDisabled={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="City"
                handleChange={() => {}}
                value={formData?.address?.city1 || ""}
                isDisabled={true}
                id="city"
                label={t("City")}
                type="text"
                placeholder={t("City")}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="Country"
                handleChange={() => {}}
                value={formData?.address?.country1 || ""}
                isDisabled={true}
                id="country"
                label={t("Country")}
                type="text"
                placeholder={t("Country")}
              />
            </div>

            {/* External Information Section */}
            <div className="col-sm-12 scrapingExternalInforWrapper">
              <LabelWithTextAreaField
                name="externalInfo"
                handleChange={handleFieldChange}
                label={t("External Info")}
                isMandatory={false}
                value={
                  formData?.advertText
                    ? formData.advertText
                    : t("No external information available")
                }
                isDisabled={true}
                className="scrollBarDesign"
              />
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="row backPadding">
          <div className="col-6 align-self-center">
            <Link
              to="/manage-scraping"
              className="back-btn text-decoration-underline"
            >
              {t("Back")}
            </Link>
          </div>
        </div>
      </form>
    </AccessControl>
  );
};

export default ViewScrapping;
